/**
 * 门户大桌面分类默认预设（出厂）
 * - 数据来源：schoolplatform.Permission（Pid=0 一级菜单）
 * - 分类名用通用业务表述，不用「某某处」行政称谓
 * - menuNames 与库内 Name 精确一致；出厂默认一菜单只归一处（运行时允许同一菜单挂多个业务域）
 * - 规则外新菜单自动落入「系统配置与运维」
 * 最后更新：2026-08-13
 */

/** 未分类兜底板块（配置页发现新增未挂接菜单时使用） */
export const UNCLASSIFIED_GROUP_TPL = {
  key: 'unclassified',
  name: '未分类',
  icon: 'el-icon-warning-outline',
  orderSort: 0,
  visible: true,
  menus: []
};

/**
 * 7 大功能板块默认预设（覆盖库内全部一级业务菜单，不含「首页」）
 */
export const DEFAULT_DEPT_GROUPS = [
  {
    key: 'info',
    name: '系统配置与运维',
    icon: 'el-icon-monitor',
    orderSort: 1,
    visible: true,
    menuNames: [
      '用户角色管理',
      '菜单权限管理',
      '日志管理',
      '系统运维',
      '系统管理',
      '任务调度',
      '微信公众号管理'
    ]
  },
  {
    key: 'equipment',
    name: '校园底座与设备',
    icon: 'el-icon-cpu',
    orderSort: 2,
    visible: true,
    menuNames: [
      '机构管理',
      '大屏设备',
      '终端管理',
      '摄像头管理',
      '电脑大屏管理',
      '通话管理'
    ]
  },
  {
    key: 'teaching',
    name: '教学与教研',
    icon: 'el-icon-notebook-1',
    orderSort: 3,
    visible: true,
    menuNames: [
      '教师考勤管理',
      '考试管理',
      '课程管理',
      '智能会议室',
      '集体备课',
      '教师巡检登记',
      '工作记录'
    ]
  },
  {
    key: 'security',
    name: '出入与安防',
    icon: 'el-icon-lock',
    orderSort: 4,
    visible: true,
    menuNames: [
      '学生考勤管理',
      '学生点到',
      '体温管理',
      '校车管理',
      '请假管理',
      '门禁管理',
      '放学大屏管理',
      '停车场管理'
    ]
  },
  {
    key: 'moral',
    name: '德育与成长',
    icon: 'el-icon-medal',
    orderSort: 5,
    visible: true,
    menuNames: [
      '人员管理',
      '德育管理',
      '积分抽奖',
      '获奖荣誉',
      '班级圈',
      '家访管理',
      '五育评价'
    ]
  },
  {
    key: 'general',
    name: '后勤校园生活',
    icon: 'el-icon-food',
    orderSort: 6,
    visible: true,
    menuNames: [
      '消费管理',
      '宿舍管理',
      '水电控管理',
      '报修管理',
      '订餐管理'
    ]
  },
  {
    key: 'principal',
    name: '公告与发布',
    icon: 'el-icon-data-analysis',
    orderSort: 7,
    visible: true,
    menuNames: [
      '公告管理',
      '智慧班牌'
    ]
  }
];

/** 不参与门户平铺的一级菜单 */
const PORTAL_SKIP_NAMES = new Set(['首页', '大桌面版面配置']);

/**
 * 根据一级菜单列表生成板块配置
 * - 精确名称匹配，一菜单只归一处
 * - 规则未命中的一级菜单默认归入「系统配置与运维」
 * @param {Array<{id:string|number, name:string}>} menuList
 * @param {{ unmatchedSinkKey?: string|null }} options
 * @returns {Array}
 */
export function buildDefaultGroupsFromMenus(menuList, options = {}) {
  const unmatchedSinkKey = Object.prototype.hasOwnProperty.call(options, 'unmatchedSinkKey')
    ? options.unmatchedSinkKey
    : 'info';

  const flat = Array.isArray(menuList) ? menuList : [];
  const used = new Set();

  const groups = DEFAULT_DEPT_GROUPS.map((g, idx) => {
    const nameSet = new Set(g.menuNames || []);
    const matchedIds = [];

    flat.forEach(m => {
      if (!m || !m.name || PORTAL_SKIP_NAMES.has(m.name)) return;
      const mid = m.id === null || m.id === undefined ? '' : String(m.id);
      if (!mid || used.has(mid)) return;
      if (!nameSet.has(m.name)) return;
      used.add(mid);
      matchedIds.push(mid);
    });

    return {
      key: g.key,
      name: g.name,
      icon: g.icon,
      orderSort: g.orderSort != null ? g.orderSort : idx + 1,
      visible: g.visible !== false,
      menus: matchedIds
    };
  });

  if (unmatchedSinkKey) {
    const sink = groups.find(g => g.key === unmatchedSinkKey);
    if (sink) {
      flat.forEach(m => {
        if (!m || !m.name || PORTAL_SKIP_NAMES.has(m.name)) return;
        const mid = m.id === null || m.id === undefined ? '' : String(m.id);
        if (!mid || used.has(mid)) return;
        used.add(mid);
        sink.menus.push(mid);
      });
    }
  }

  return groups;
}
